import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Button,
  Typography,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useTheme } from "@mui/material/styles";

import SimpleList, { SimpleListItem } from "components/Data/DataDisplays/SimpleList";
import SimpleListController from "components/Data/DataDisplays/SimpleListController";
import MoveConfirmDialog from "components/Flow/Tabs/ItemMovementTabs/MoveConfirmDialog";

type HeaderIconButton = {
  icon: React.ReactNode;
  onClick: () => void;
  tooltip?: string;
  ariaLabel?: string;
};

interface ActionOnRecordProps<T extends SimpleListItem> {
  fetchData: () => Promise<T[]>;
  secondaryData?: T[];
  onAction: (selected: T[]) => Promise<void> | void;

  primaryColor?: string;
  textColor?: string;

  externalLink?: string;
  actionLabel?: string;
  confirmationDialogue?: boolean;
  pageSize?: number;

  title?: string;
  headerIconButton?: HeaderIconButton;

  showMoreLabel?: string;
  defaultShowMore?: boolean;
}

const ActionOnRecord = <T extends SimpleListItem>({
  fetchData,
  secondaryData,
  onAction,
  primaryColor: primaryColorProp,
  textColor: textColorProp,
  externalLink,
  actionLabel = "Perform Action",
  confirmationDialogue = true,
  pageSize = 20,
  title,
  headerIconButton,
  showMoreLabel = "Show More",
  defaultShowMore = false,
}: ActionOnRecordProps<T>) => {
  const theme = useTheme();

  const primaryColor = primaryColorProp ?? theme.palette.primary.main;
  const textColor = textColorProp ?? theme.palette.text.primary;

  const [primaryItems, setPrimaryItems] = useState<T[]>([]);
  const [secondaryItems, setSecondaryItems] = useState<T[]>([]);
  const [selected, setSelected] = useState<(string | number)[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingItems, setPendingItems] = useState<T[] | null>(null);

  // ✅ separate pagination
  const [primaryPage, setPrimaryPage] = useState(1);
  const [secondaryPage, setSecondaryPage] = useState(1);

  const [showMore, setShowMore] = useState(defaultShowMore);
  const hasSecondary = (secondaryData?.length ?? 0) > 0;

  const loadData = async () => {
    setLoading(true);
    try {
      const primary = await fetchData();
      const secondary = secondaryData ?? [];

      const primaryIDs = new Set(primary.map((p) => p.id));
      const cleanedSecondary = secondary.filter((s) => !primaryIDs.has(s.id));

      const sortedPrimary = [...primary].sort((a, b) => a.primary.localeCompare(b.primary));
      const sortedSecondary = [...cleanedSecondary].sort((a, b) => a.primary.localeCompare(b.primary));

      setPrimaryItems(sortedPrimary);
      setSecondaryItems(sortedSecondary);

      setSelected([]);
      setPrimaryPage(1);
      setSecondaryPage(1);
    } catch (err) {
      console.error("Error loading data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData, secondaryData]);

  // ✅ filter each list separately, but using same search
  const filteredPrimary = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return primaryItems;
    return primaryItems.filter(
      (i) =>
        i.primary.toLowerCase().includes(q) ||
        (i.secondary?.toLowerCase() ?? "").includes(q)
    );
  }, [search, primaryItems]);

  const filteredSecondary = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return secondaryItems;
    return secondaryItems.filter(
      (i) =>
        i.primary.toLowerCase().includes(q) ||
        (i.secondary?.toLowerCase() ?? "").includes(q)
    );
  }, [search, secondaryItems]);

  // if search changes, reset pages
  useEffect(() => {
    setPrimaryPage(1);
    setSecondaryPage(1);
  }, [search]);

  // visible items = primary + (secondary if showMore)
  const visibleItems = useMemo(() => {
    return showMore ? [...filteredPrimary, ...filteredSecondary] : filteredPrimary;
  }, [showMore, filteredPrimary, filteredSecondary]);

  // keep selection only for visible ids
  useEffect(() => {
    const visibleIds = new Set(visibleItems.map((i) => i.id));
    setSelected((prev) => prev.filter((id) => visibleIds.has(id)));
  }, [visibleItems]);

  const toggleSelect = (id: string | number) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const selectAll = () => {
    if (selected.length === visibleItems.length) setSelected([]);
    else setSelected(visibleItems.map((i) => i.id));
  };

  const handleAction = async () => {
    const selectedItems = visibleItems.filter((i) => selected.includes(i.id));

    if (confirmationDialogue) {
      setPendingItems(selectedItems);
      setConfirmOpen(true);
      return;
    }

    setProcessing(true);
    try {
      await onAction(selectedItems);
    } catch (err) {
      console.error("Action failed:", err);
    } finally {
      await loadData();
      setProcessing(false);
    }
  };

  const confirmAction = async () => {
    if (!pendingItems) return;

    setConfirmOpen(false);
    setProcessing(true);
    try {
      await onAction(pendingItems);
    } catch (err) {
      console.error("Action failed:", err);
    } finally {
      await loadData();
      setProcessing(false);
      setPendingItems(null);
    }
  };

  const cancelAction = () => {
    setConfirmOpen(false);
    setPendingItems(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // ✅ pagination for each list separately
  const primaryTotalPages = Math.max(1, Math.ceil(filteredPrimary.length / pageSize));
  const primaryPaginated = filteredPrimary.slice((primaryPage - 1) * pageSize, primaryPage * pageSize);

  const secondaryTotalPages = Math.max(1, Math.ceil(filteredSecondary.length / pageSize));
  const secondaryPaginated = filteredSecondary.slice(
    (secondaryPage - 1) * pageSize,
    secondaryPage * pageSize
  );

  const Pagination = ({
    totalPages,
    page,
    setPage,
  }: {
    totalPages: number;
    page: number;
    setPage: (p: number) => void;
  }) => {
    if (totalPages <= 1) return null;
    return (
      <Box display="flex" justifyContent="center" gap={1} mt={2}>
        {Array.from({ length: totalPages }).map((_, i) => {
          const pageNum = i + 1;
          const isActive = page === pageNum;
          return (
            <Button
              key={pageNum}
              size="small"
              variant={isActive ? "contained" : "outlined"}
              color="primary"
              sx={{ minWidth: 32, padding: "2px 6px" }}
              onClick={() => setPage(pageNum)}
            >
              {pageNum}
            </Button>
          );
        })}
      </Box>
    );
  };
  
  return (
    <Box sx={{ height: "100%", overflow: "auto" }}>
      {/* Title row with optional icon button */}
      {(title || headerIconButton) && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="h6">{title ?? ""}</Typography>

          {headerIconButton && (
            <Tooltip title={headerIconButton.tooltip ?? ""}>
              <IconButton
                onClick={headerIconButton.onClick}
                aria-label={headerIconButton.ariaLabel ?? "header action"}
                size="small"
                sx={{
                  bgcolor: primaryColor,
                  color: "#fff",
                  "&:hover": { bgcolor: primaryColor, opacity: 0.9 },
                }}
              >
                {headerIconButton.icon}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      <SimpleListController
        search={search}
        onSearchChange={setSearch}
        selectedCount={selected.length}
        totalCount={visibleItems.length}
        onSelectAll={selectAll}
        onAction={handleAction}
        loadingAction={processing}
        actionLabel={actionLabel}
        externalLink={externalLink}
        primaryColor={primaryColor}
        textColor={textColor}
      />

      {/* ✅ PRIMARY LIST */}
      <SimpleList
        items={primaryPaginated}
        selected={selected}
        onToggleSelect={toggleSelect}
        showCheckbox
        primaryColor={primaryColor}
      />

      {/* Pagination ONLY for primary unless showMore reveals secondary */}
      <Pagination totalPages={primaryTotalPages} page={primaryPage} setPage={setPrimaryPage} />

      {/* ✅ Small toggle BETWEEN primary and secondary */}
      {hasSecondary && (
        <Box sx={{ mt: 1, mb: showMore ? 1 : 0 }}>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              size="small"
              variant="text"
              onClick={() => setShowMore((v) => !v)}
              startIcon={showMore ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ textTransform: "none", fontSize: 12, py: 0.25 }}
            >
              {showMore ? "Show Less" : showMoreLabel}
            </Button>
          </Box>
          <Divider sx={{ my: 1 }} />
        </Box>
      )}

      {/* ✅ SECONDARY LIST (only if showMore) */}
      {showMore && hasSecondary && (
        <>
          <SimpleList
            items={secondaryPaginated}
            selected={selected}
            onToggleSelect={toggleSelect}
            showCheckbox
            primaryColor={primaryColor}
          />
          <Pagination
            totalPages={secondaryTotalPages}
            page={secondaryPage}
            setPage={setSecondaryPage}
          />
        </>
      )}

      <MoveConfirmDialog open={confirmOpen} onConfirm={confirmAction} onCancel={cancelAction} />
    </Box>
  );
};

export default ActionOnRecord;
